import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { ArrowRight, ShoppingCart, X, Check, Plus, Minus, Trash2 } from 'lucide-react'
import { getSession } from '../lib/session'
import { CATALOG, byKey, unitOf } from '../lib/serviceCatalog'
import { clusterFor, productHref, groupByCluster } from './marketingMeta'
import MarketingNav from './MarketingNav'
import SiteFooter from './SiteFooter'

// Buyer-outcome question per lane — leads discovery with the problem, not the feature.
const LANE_Q = {
  Answer: 'Can we answer customers accurately, around the clock?',
  Build: 'Can we automate repetitive work and build on our own data — safely?',
  Sell: 'Can we convert more of the conversations we already have?',
  Grow: "What's driving revenue, and where should we act next?",
  Resell: 'Can we run the whole platform under our own brand?',
}
import { getCart, addToCart, setQty, removeFromCart } from '../lib/cart'
import './landing.css'

/**
 * Public landing for ai.reright.io — a DARK CINEMATIC, headless home ("v9").
 *
 * The whole page is scoped under a `.rr9` wrapper (see landing.css) so its
 * bespoke dark token system never leaks into the rest of the app (which runs a
 * light/dark CSS-variable theme). The shared MarketingNav / SiteFooter are NOT
 * used here — a dark nav + footer are built inline so other routes are untouched.
 *
 * HEADLESS (preserved): copy comes from the CMS (GET /api/v1/site/home →
 * { content, prices }). The fetch NEVER throws — missing fields fall back to the
 * defaults below, and `prices` override the catalog price by key. The hero
 * (eyebrow / title / lead / CTAs) and the "keeps answers useful" proof strip are
 * driven from content.*; sections the new design adds with no CMS field are
 * sensible hardcoded defaults.
 *
 * INTERACTIVE (preserved): the services catalog IS the interactive commerce grid
 * — CATALOG → clickable card → ServiceModal → add to cart → CartDrawer →
 * /checkout, plus the cart badge in the nav. Sign-in lives on its own dedicated
 * /signin page now, so this home is pure marketing — no embedded auth card.
 * Animations (constellation canvas, magnet CTA, scroll-driven marquee /
 * manifesto / sticky deck, reveal-on-scroll) are all driven from a single
 * prefers-reduced-motion-guarded effect.
 */

// ── Built-in fallbacks (used when the CMS is cold or a field is missing) ──────
const DEFAULT_CONTENT = {
  hero: {
    eyebrow: 'One platform · every service, one login · five lanes',
    title: 'AI that answers, sells, and grows — built on your data.',
    lead: 'Bring your website, docs and systems once — then switch on a support chatbot, a voice receptionist, a private RAG API, or a marketing brain. Each answers only from your own sources, verified before it speaks, in 95+ languages.',
    primaryCta: { label: 'Explore AI services', href: '#services' },
    secondaryCta: { label: 'See how it works', href: '#useful' },
  },
  sections: {
    // The "How it keeps answers useful & safe" proof strip — headless (5 steps).
    proof: [
      { b: 'Step 01', t: 'Connect your approved sources — site, docs, systems.' },
      { b: 'Step 02', t: "Retrieve the material that's actually relevant." },
      { b: 'Step 03', t: 'Verify the evidence before it replies.' },
      { b: 'Step 04', t: 'Cite the sources — or safely abstain.' },
      { b: 'Step 05', t: 'Route anything unresolved to your team.' },
    ],
    // Kept for CMS shape-compatibility (not rendered by the v9 design).
    values: [],
    howItWorks: [],
    faq: [],
  },
  // Headless homepage sections (admin/agent-editable via the home override).
  outcomes: [
    { label: 'Answer customers', sub: '24/7 support, grounded in your content', href: '#services' },
    { label: 'Capture & route leads', sub: 'Qualify every enquiry, route to the right desk', href: '/products/multi-specialty-intake' },
    { label: 'Sell in chat', sub: 'Turn conversations into orders', href: '/products/whatsapp-commerce' },
    { label: 'Automate team workflows', sub: 'Agentic automations across your systems', href: '/products/agentic-workflows' },
    { label: 'Understand marketing & SEO performance', sub: 'Evidence-first marketing intelligence', href: '/products/marketing-intelligence-brain' },
  ],
  evidence: {
    label: 'Marketing intelligence',
    heading: 'An answer you can trace.',
    eyebrow: 'Example',
    question: 'Why did qualified leads decline this month?',
    answer: "Meta spend rose while the campaign landing page's conversion rate fell, concentrated on mobile visitors — review the page, cut competing CTAs, and shift budget to the two higher-quality campaigns.",
    caption: 'Illustrative · source-linked answer from ads, analytics & CRM.',
  },
  deckLead: 'A live look at what a few of your services actually do. More lanes, more services — added over time.',
}

// Coerce whatever the CMS returns into a safe, fully-populated shape.
function normalizeContent(raw) {
  const d = DEFAULT_CONTENT
  const c = raw || {}
  const hero = c.hero || {}
  const secs = c.sections || {}
  const okArr = (v) => Array.isArray(v) && v.length > 0
  return {
    hero: {
      eyebrow: hero.eyebrow || d.hero.eyebrow,
      title: hero.title || d.hero.title,
      lead: hero.lead || d.hero.lead,
      primaryCta: hero.primaryCta?.label ? hero.primaryCta : d.hero.primaryCta,
      secondaryCta: hero.secondaryCta?.label ? hero.secondaryCta : d.hero.secondaryCta,
    },
    sections: {
      proof: okArr(secs.proof) ? secs.proof : d.sections.proof,
      values: okArr(secs.values) ? secs.values : d.sections.values,
      howItWorks: okArr(secs.howItWorks) ? secs.howItWorks : d.sections.howItWorks,
      faq: okArr(secs.faq) ? secs.faq : d.sections.faq,
    },
    outcomes: okArr(c.outcomes) ? c.outcomes : d.outcomes,
    evidence: (c.evidence && typeof c.evidence === 'object' && c.evidence.question) ? { ...d.evidence, ...c.evidence } : d.evidence,
    deckLead: (typeof c.deckLead === 'string' && c.deckLead.trim()) ? c.deckLead : d.deckLead,
  }
}

// ── Non-headless design copy (structure the v9 design adds; no CMS field) ─────
const MARQUEE_SERVICES = [
  'AI Support Chatbot', 'Voice AI Receptionist', 'Custom RAG API', 'Multi-Specialty Intake',
  'Knowledge Sync', 'Agentic Workflows', 'Marketing Intelligence', 'Growth Lab',
  'SEO Intelligence', 'WhatsApp Commerce', 'Team Inbox', 'Tool-using Agent · MCP',
  'White-label / Reseller', 'Integration Adapters', 'Content Studio',
]

const MANIFESTO_TEXT = 'Your content — answering, selling, growing. Verified before it speaks, in any language, on every channel.'
const MANIFESTO_ACCENT = ['answering,', 'selling,', 'growing.']

const DECK = [
  {
    num: '01 · Voice AI Receptionist · $89/mo',
    gif: '/media/marketing/voice-routing.gif', alt: 'Voice AI routing a live call',
    pre: 'It answers your phone, ', serif: 'routes every call.', post: '',
    p: 'The AI picks up, works out what the caller needs in their own language, routes them to the right desk, and hands off to a human whenever it is unsure.',
    bullets: [
      'Twilio-native — no new phone system',
      'Grounded in your content — abstains when unsure',
      'Clean hand-off with full context',
    ],
    tagB: 'Answer lane', tag: ' · the 15th billable service',
  },
  {
    num: '02 · Answer Trust · built in',
    gif: '/media/marketing/trust-loop.gif', alt: 'Answer verified before it ships',
    pre: 'Every answer ', serif: 'verified', post: ' before it ships.',
    p: 'Each reply is graded for groundedness against your own sources, gets a confidence score and citations, and is held back or routed to a human when support is thin.',
    bullets: [
      'Groundedness score on every reply',
      'Ungrounded claims → safe fallback',
      'Append-only, auditable, PII-redacted trail',
    ],
    tagB: 'Trust layer', tag: ' · no extra charge',
  },
  {
    num: '03 · Multilingual · built in',
    gif: '/media/marketing/multilingual.gif', alt: "Replies in the visitor's language",
    pre: 'It replies in ', serif: 'any language,', post: ' automatically.',
    p: "It detects your visitor's language with zero setup and answers in it — across chat and voice, with no per-language configuration.",
    bullets: [
      '95+ languages, one assistant',
      'Detection is free — English pays nothing',
      'Pluggable providers keep costs down',
    ],
    tagB: 'Built into every conversation', tag: '',
  },
  {
    num: '04 · Custom RAG API · $29/mo',
    gif: '/media/marketing/agentic-rag.gif', alt: 'Self-correcting retrieval',
    pre: 'It ', serif: 'checks its own work,', post: ' then answers.',
    p: "Retrieve → grade → rewrite → answer. When the context isn't good enough it re-searches before it speaks — cheap when confident, deeper when it isn't.",
    bullets: [
      'Answers with citations you can trace',
      'Adapts depth to its own confidence',
      'Never breaks — degrades gracefully',
    ],
    tagB: 'Answer lane', tag: ' · a private Q&A brain',
  },
  {
    num: '05 · The agent mesh · powers all 15',
    mesh: true,
    pre: 'A team of agents that ', serif: 'hand off.', post: '',
    p: 'A Chief agent oversees ~20 specialists — retrieval, routing, verification, escalation — dispatching the right one for every request and logging every hop.',
    bullets: [
      'Every dispatch logged and auditable',
      'Live mesh view in your console',
      'Scales on demand, stops when idle',
    ],
    tagB: 'Agentic architecture', tag: ' · the whole platform',
  },
]

// ── Small inline SVGs (match the v9 stroke set) ──────────────────────────────
const CheckMark = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6L9 17l-5-5" /></svg>
)
const HERO_CHIPS = [
  { label: 'Built on your data', path: <path d="M21 16V8a2 2 0 0 0-1-1.7l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.7l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /> },
  { label: 'Source-linked answers', path: <path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1" /> },
  { label: '95+ languages', path: <><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18" /></> },
  { label: 'Human handoff', path: <path d="M16 11a4 4 0 1 0-8 0M4 21v-1a4 4 0 0 1 4-4h1m6 0h1a4 4 0 0 1 4 4v1M18 8l2 2 3-3" /> },
]
const TRUST_ITEMS = [
  { label: 'Every answer verified', path: <path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6z" /> },
  { label: 'PII-redacted audit trail', path: <><rect x="4" y="4" width="16" height="16" rx="2" /><path d="M9 9h6M9 13h6M9 17h3" /></> },
  { label: '95+ languages', path: <><circle cx="12" cy="12" r="9" /><path d="M3 12h18" /></> },
  { label: 'Your data stays yours', path: <><rect x="4" y="10" width="16" height="10" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></> },
  { label: 'Pay only for what you turn on', path: <path d="M20 6L9 17l-5-5" /> },
]

// Five buyer outcomes (the "five beats"), each a real link into its lane/product.
// `hash` → smooth-scroll to a section; `to` → route to the product page. Rendered
// as an <a>/<Link> respectively so every choice is a real, crawlable link.
// The five outcome cards are HEADLESS — see DEFAULT_CONTENT.outcomes and the
// `content.outcomes` used in the render (admin/agent-editable via the home override).

// ── Mark (R|Я) reused by nav + footer ────────────────────────────────────────
const Mark = () => (
  <span className="mark"><span className="r1">R</span><span className="bar">|</span><span className="r2">R</span></span>
)

// Agent-mesh SVG art (deck card 5 + philosophy split). `chief` adds the label.
function MeshArt({ chief = false, style }) {
  return (
    <div className="mesh-wrap">
      <svg className="mesh-svg" viewBox="0 0 300 220" fill="none" style={style}>
        <line className="mesh-line mesh-spoke s1" x1="150" y1="110" x2="250" y2="40" />
        <line className="mesh-line mesh-spoke s2" x1="150" y1="110" x2="250" y2="90" />
        <line className="mesh-line mesh-spoke s3" x1="150" y1="110" x2="250" y2="140" />
        <line className="mesh-line mesh-spoke s4" x1="150" y1="110" x2="250" y2="190" />
        <line className="mesh-pulse p1" x1="150" y1="110" x2="250" y2="40" />
        <line className="mesh-pulse p2" x1="150" y1="110" x2="250" y2="90" />
        <line className="mesh-pulse p3" x1="150" y1="110" x2="250" y2="140" />
        <line className="mesh-pulse p4" x1="150" y1="110" x2="250" y2="190" />
        <circle className="mesh-hub" cx="150" cy="110" r="20" fill="none" stroke="#e5372b" strokeWidth="2" />
        <text x="150" y="115" fill="#ff6a5c" fontSize="15" textAnchor="middle" fontFamily="Geist" fontWeight="600">RЯ</text>
        {chief && <text x="150" y="150" fill="#a4abb9" fontSize="10" textAnchor="middle" fontFamily="Geist">Chief</text>}
        <g fontFamily="Geist" fontSize="10.5" fill="#c7cdd8">
          <rect x="252" y="30" width="44" height="20" rx="6" fill="#12151d" stroke="#2a3038" /><text x="274" y="43" textAnchor="middle">Retrieve</text>
          <rect x="252" y="80" width="44" height="20" rx="6" fill="#12151d" stroke="#2a3038" /><text x="274" y="93" textAnchor="middle">Route</text>
          <rect x="252" y="130" width="44" height="20" rx="6" fill="#12151d" stroke="#2a3038" /><text x="274" y="143" textAnchor="middle">Verify</text>
          <rect x="252" y="180" width="44" height="20" rx="6" fill="#12151d" stroke="#2a3038" /><text x="274" y="193" textAnchor="middle">Escalate</text>
        </g>
      </svg>
    </div>
  )
}

// Hero headline: word-by-word "pull" reveal, driven from the (headless) title.
// Anything after an em-dash renders on a new line in the Instrument Serif italic.
function HeroHeadline({ title }) {
  const idx = title.indexOf('—')
  const head = (idx >= 0 ? title.slice(0, idx) : title).trim()
  const tail = idx >= 0 ? title.slice(idx + 1).trim() : ''
  const headWords = head.split(/\s+/).filter(Boolean)
  let delay = 0.05
  const headEls = headWords.map((w, i) => {
    const d = delay; delay += 0.05
    return (
      <React.Fragment key={`h${i}`}>
        <span className="pull"><span style={{ animationDelay: `${d}s` }}>{w}</span></span>{' '}
      </React.Fragment>
    )
  })
  return (
    <>
      {headEls}
      {tail && (
        <>
          <span className="serif" style={{ opacity: 0.5 }}>—</span><br />
          <span className="serif pull"><span style={{ animationDelay: `${delay + 0.05}s` }}>{tail}</span></span>
        </>
      )}
    </>
  )
}

// Manifesto text broken into per-character spans (scroll lights them up).
function ManifestoChars({ text, accent }) {
  const words = text.split(' ')
  const out = []
  words.forEach((word, wi) => {
    const isAcc = accent.includes(word)
    for (let c = 0; c < word.length; c++) {
      out.push(<span key={`${wi}-${c}`} className={`ch${isAcc ? ' accent' : ''}`}>{word[c]}</span>)
    }
    if (wi < words.length - 1) out.push(<span key={`${wi}-sp`} className="ch">{' '}</span>)
  })
  return <>{out}</>
}

export default function Landing() {
  // NOTE: sign-in now lives on a dedicated /signin page. This home is pure
  // marketing — it takes no `authSlot` and renders no embedded auth card.
  const authed = !!getSession()?.apiKey
  const navigate = useNavigate()

  const [content, setContent] = useState(DEFAULT_CONTENT)
  const [prices, setPrices] = useState({})
  const [catalogRows, setCatalogRows] = useState(null) // live headless catalog; null → static CATALOG
  const [modalKey, setModalKey] = useState(null)
  const [cartOpen, setCartOpen] = useState(false)
  const [cart, setCart] = useState(getCart())

  // refs for imperative animations (constellation, magnet, scroll-driven bits)
  const rootRef = useRef(null)
  const meshRef = useRef(null)
  const magnetRef = useRef(null)
  const navRef = useRef(null)
  const deckRef = useRef(null)
  const mqSecRef = useRef(null)
  const row1Ref = useRef(null)
  const row2Ref = useRef(null)
  const revealRef = useRef(null)

  // Live cart sync.
  useEffect(() => {
    const sync = () => setCart(getCart())
    window.addEventListener('reright-cart', sync)
    return () => window.removeEventListener('reright-cart', sync)
  }, [])

  // Headless content + live prices. Never throws — defaults stand on failure.
  useEffect(() => {
    let alive = true
    axios.get('/api/v1/site/home')
      .then(({ data }) => {
        if (!alive || !data) return
        setContent(normalizeContent(data.content))
        if (data.prices && typeof data.prices === 'object') setPrices(data.prices)
      })
      .catch(() => { /* keep defaults */ })
    return () => { alive = false }
  }, [])

  // Headless product LIST — the live catalog (static seed + agent/admin registry).
  // Falls back to the static CATALOG until it responds, so the grid never blanks.
  useEffect(() => {
    let alive = true
    axios.get('/api/v1/site/catalog')
      .then(({ data }) => {
        if (alive && data && Array.isArray(data.services) && data.services.length) setCatalogRows(data.services)
      })
      .catch(() => { /* keep static CATALOG */ })
    return () => { alive = false }
  }, [])

  // Live price override, merged by service key (row price → catalog default fallback).
  const priceOf = useCallback((k) => (prices[k] != null ? prices[k] : (byKey(k)?.price ?? 0)), [prices])

  // The interactive services grid. When the live catalog is present we drive the
  // LIST from it (so agent/admin-registered products show up + hidden ones drop),
  // enriching each with the local catalog's icon + rich modal copy. Products not in
  // the local catalog (brand-new) still render — icon + copy degrade gracefully.
  const services = (Array.isArray(catalogRows) && catalogRows.length)
    ? catalogRows.map((r) => {
        const local = byKey(r.key) || {}
        const price = prices[r.key] != null ? prices[r.key] : (r.price != null ? r.price : (local.price ?? 0))
        return {
          ...local,
          key: r.key,
          slug: r.slug || local.slug,
          cluster: r.cluster || clusterFor(r.key),
          name: r.name || r.keyword || local.name || r.key,
          short: r.short || local.short || '',
          desc: local.desc || r.short || '',
          icon: local.icon, // undefined for new products → guarded at render
          seated: local.seated,
          price,
          _new: !byKey(r.key),
        }
      })
    : CATALOG.map((s) => ({ ...s, price: priceOf(s.key) }))

  // Resolve a service by key across the LIVE list (falls back to the static catalog),
  // so cart lines + the modal work for registry-added products too.
  const byKeyLive = (k) => services.find((s) => s.key === k) || byKey(k)

  const items = cart.map((c) => { const s = byKeyLive(c.key); return s ? { ...s, price: priceOf(c.key), qty: c.qty } : null }).filter(Boolean)
  const total = items.reduce((s, x) => s + x.price * x.qty, 0)
  const seatCount = cart.reduce((n, c) => n + c.qty, 0)
  const modalSvc = modalKey ? { ...(byKeyLive(modalKey) || {}), price: priceOf(modalKey) } : null
  const qtyInCart = (k) => cart.find((c) => c.key === k)?.qty || 0
  const goCheckout = () => { setCartOpen(false); setModalKey(null); navigate('/checkout') }

  const scrollToHash = useCallback((href) => {
    const el = typeof href === 'string' && href.startsWith('#') ? document.querySelector(href) : null
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])
  const goHref = useCallback((href) => {
    if (typeof href !== 'string') return
    if (href.startsWith('#')) scrollToHash(href)
    else if (href === '/guide') navigate('/guide')
    else if (href.startsWith('/')) navigate(href)
    else window.location.href = href
  }, [navigate, scrollToHash])

  // Nav "Sign in" (or "Open console" when authed) routes to the dedicated page.
  const signIn = { to: '/signin', label: authed ? 'Open console' : 'Sign in', router: true }

  const { hero, sections } = content

  // ── Single animation effect (guarded by prefers-reduced-motion) ────────────
  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    const reduce = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)
    const cleanups = []

    // reveal-on-scroll (IntersectionObserver + rAF + setTimeout fallback so the
    // above-the-fold hero can never render blank).
    const revealEls = Array.from(root.querySelectorAll('.rise,.scaleIn'))
    const io = new IntersectionObserver((es) => {
      es.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target) } })
    }, { threshold: 0.1, rootMargin: '0px 0px -6% 0px' })
    revealEls.forEach((el) => io.observe(el))
    cleanups.push(() => io.disconnect())
    const raf1 = requestAnimationFrame(() => {
      revealEls.forEach((el) => { if (el.getBoundingClientRect().top < window.innerHeight * 0.92) { el.classList.add('in'); io.unobserve(el) } })
    })
    const t1 = setTimeout(() => {
      revealEls.forEach((el) => { if (el.getBoundingClientRect().top < window.innerHeight * 0.95) el.classList.add('in') })
    }, 450)
    cleanups.push(() => { cancelAnimationFrame(raf1); clearTimeout(t1) })

    // manifesto characters
    const chars = revealRef.current ? Array.from(revealRef.current.querySelectorAll('.ch')) : []
    if (reduce) chars.forEach((c) => c.classList.add('lit'))

    // sticky deck cards
    const stack = deckRef.current
    const cards = stack ? Array.from(stack.querySelectorAll('.scard')) : []
    const N = cards.length

    const mqsec = mqSecRef.current, r1 = row1Ref.current, r2 = row2Ref.current
    const rv = revealRef.current, navEl = navRef.current
    let last = 0

    function onScroll() {
      const vh = window.innerHeight
      const narrow = window.innerWidth < 820
      if (mqsec && r1 && r2) {
        const mr = mqsec.getBoundingClientRect()
        const off = (vh - mr.top) * 0.34
        r1.style.transform = `translateX(${-off - 120}px)`
        r2.style.transform = `translateX(${off - 780}px)`
      }
      if (rv && chars.length && !reduce) {
        const rr = rv.getBoundingClientRect()
        let p = (vh * 0.82 - rr.top) / (rr.height + vh * 0.5)
        p = Math.max(0, Math.min(1, p))
        const n = Math.floor(p * chars.length)
        for (let j = 0; j < chars.length; j++) chars[j].classList.toggle('lit', j <= n)
      }
      if (stack && N && !reduce && !narrow) {
        const sr = stack.getBoundingClientRect()
        const totalH = stack.offsetHeight - vh
        const gp = Math.max(0, Math.min(1, (-sr.top) / (totalH || 1)))
        for (let i = 0; i < N; i++) {
          const start = i / N
          const local = Math.max(0, Math.min(1, (gp - start) / ((1 - start) || 1)))
          const target = 1 - (N - 1 - i) * 0.05
          const sc = 1 + (target - 1) * local
          cards[i].style.transform = `scale(${sc.toFixed(4)})`
          cards[i].style.opacity = (1 - local * 0.34).toFixed(3)
        }
      } else if (stack && narrow) {
        cards.forEach((c) => { c.style.transform = 'none'; c.style.opacity = '1' })
      }
      if (navEl) {
        const y = window.scrollY
        navEl.style.transform = (y > last && y > 150) ? 'translateY(-130%)' : 'none'
        last = y
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    onScroll()
    cleanups.push(() => { window.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onScroll) })

    // magnet cursor-follow + canvas parallax target (one mousemove)
    const mag = magnetRef.current
    let tmx = 0, tmy = 0
    function onMove(e) {
      tmx = (e.clientX / window.innerWidth - 0.5)
      tmy = (e.clientY / window.innerHeight - 0.5)
      if (mag) {
        const r = mag.getBoundingClientRect()
        const cx = r.left + r.width / 2, cy = r.top + r.height / 2
        const dx = e.clientX - cx, dy = e.clientY - cy
        const dist = Math.hypot(dx, dy)
        if (dist < r.width / 2 + 120) {
          mag.style.transition = 'transform .12s ease-out'
          mag.style.transform = `translate(${dx / 2.6}px,${dy / 2.6}px)`
        } else {
          mag.style.transition = 'transform .55s ease-in-out'
          mag.style.transform = 'translate(0,0)'
        }
      }
    }
    if (!reduce) {
      window.addEventListener('mousemove', onMove)
      cleanups.push(() => window.removeEventListener('mousemove', onMove))
    }

    // constellation canvas
    const cv = meshRef.current
    if (cv) {
      const ctx = cv.getContext('2d')
      const DPR = Math.min(window.devicePixelRatio || 1, 2)
      let W = 0, H = 0, nodes = [], hub = { x: 0, y: 0 }, t = 0, mx = 0, my = 0
      let rafId = null, running = true
      function build() {
        nodes = []
        hub = { x: W * 0.5, y: H * 0.42 }
        const count = W < 700 ? 12 : 20
        for (let i = 0; i < count; i++) {
          const ring = 100 + (i % 3) * 78 + (i * 7) % 50
          const a = (i / count) * Math.PI * 2 + i * 0.6
          nodes.push({ baseA: a, ring, spd: 0.00008 + (i % 4) * 0.00003, r: 1.6 + (i % 3), pulse: i * 140 % 1000, depth: 0.4 + (i % 3) * 0.3 })
        }
      }
      function resize() {
        W = cv.clientWidth; H = cv.clientHeight
        cv.width = W * DPR; cv.height = H * DPR
        ctx.setTransform(DPR, 0, 0, DPR, 0, 0)
        build()
      }
      function draw() {
        t += 1; mx += (tmx - mx) * 0.05; my += (tmy - my) * 0.05
        ctx.clearRect(0, 0, W, H)
        const hx = hub.x + mx * 34, hy = hub.y + my * 24
        const g = ctx.createRadialGradient(hx, hy, 0, hx, hy, Math.max(W, H) * 0.7)
        g.addColorStop(0, 'rgba(229,55,43,0.1)'); g.addColorStop(0.5, 'rgba(229,55,43,0.02)'); g.addColorStop(1, 'rgba(6,7,9,0)')
        ctx.fillStyle = g; ctx.fillRect(0, 0, W, H)
        nodes.forEach((n) => {
          const a = n.baseA + t * n.spd * 130
          const x = hx + Math.cos(a) * n.ring + mx * 40 * n.depth
          const y = hy + Math.sin(a) * n.ring * 0.62 + my * 30 * n.depth
          ctx.beginPath(); ctx.moveTo(hx, hy); ctx.lineTo(x, y); ctx.strokeStyle = 'rgba(255,255,255,0.05)'; ctx.lineWidth = 1; ctx.stroke()
          const ph = ((t + n.pulse) % 260) / 260
          if (ph < 0.5) { const pp = ph / 0.5, px = hx + (x - hx) * pp, py = hy + (y - hy) * pp; ctx.beginPath(); ctx.arc(px, py, 2.2, 0, 7); ctx.fillStyle = `rgba(255,106,92,${0.9 * (1 - pp)})`; ctx.fill() }
          ctx.beginPath(); ctx.arc(x, y, n.r, 0, 7); ctx.fillStyle = 'rgba(214,220,232,0.55)'; ctx.fill()
        })
        const hg = ctx.createRadialGradient(hx, hy, 0, hx, hy, 50)
        hg.addColorStop(0, 'rgba(229,55,43,0.5)'); hg.addColorStop(1, 'rgba(229,55,43,0)')
        ctx.fillStyle = hg; ctx.beginPath(); ctx.arc(hx, hy, 50, 0, 7); ctx.fill()
        ctx.beginPath(); ctx.arc(hx, hy, 6, 0, 7); ctx.fillStyle = '#e5372b'; ctx.fill()
        if (running && !reduce) rafId = requestAnimationFrame(draw)
      }
      const onResize = () => resize()
      window.addEventListener('resize', onResize)
      resize()
      if (reduce) draw(); else rafId = requestAnimationFrame(draw)
      cleanups.push(() => { running = false; if (rafId) cancelAnimationFrame(rafId); window.removeEventListener('resize', onResize) })
    }

    return () => cleanups.forEach((fn) => fn())
  }, [])

  const marqueePills = (
    <>
      {[0, 1, 2].map((rep) => MARQUEE_SERVICES.map((s, i) => (
        <span className="mq glass" key={`${rep}-${i}`}><span className="d" />{s}</span>
      )))}
    </>
  )

  return (
    <div className="rr9 mkt" ref={rootRef}>
      <div className="grain" />

      {/* Shared site nav — same MarketingNav (mega + Use cases + cart + sign in) as
          every other marketing page, so the whole site reads as one product. */}
      <MarketingNav
        services={services}
        cartCount={seatCount}
        onOpenCart={() => setCartOpen(true)}
        signIn={signIn}
        onHash={scrollToHash}
      />

      {/* HERO */}
      <div id="top" />
      <section className="hero">
        <canvas className="mesh" ref={meshRef} />
        <div className="hero-fade" />
        <div className="hero-inner wrap">
          <div className="eyebrow"><span className="dot" /> {hero.eyebrow}</div>
          <h1 className="hero-h"><HeroHeadline title={hero.title} /></h1>
          <p className="hero-lede rise" style={{ animationDelay: '.55s' }}>{hero.lead}</p>
          <div className="magnet-wrap rise" style={{ animationDelay: '.7s' }}>
            <button className="magnet" ref={magnetRef} onClick={() => goHref(hero.primaryCta.href)}>
              <span className="ic">✦</span>
              <span>{(hero.primaryCta.label || 'Explore AI services').split(' ').map((w, i, a) => <React.Fragment key={i}>{w}{i < a.length - 1 ? <br /> : null}</React.Fragment>)} →</span>
            </button>
          </div>
          <div className="rise" style={{ animationDelay: '.8s', marginTop: '.2rem' }}>
            <a className="see-how" onClick={() => goHref(hero.secondaryCta.href)}>{hero.secondaryCta.label} →</a>
          </div>
          <div className="meta-row rise" style={{ animationDelay: '.9s' }}>
            {HERO_CHIPS.map((c, i) => (
              <span className="chip glass" key={i}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{c.path}</svg>{c.label}
              </span>
            ))}
          </div>
        </div>
        <div className="scrollcue"><span>Scroll</span><span className="line" /></div>
      </section>

      {/* OUTCOME SELECTOR — makes the five-beats framework actionable. Five real
          links (scroll-into-catalog or a representative product page). */}
      <section className="oc-sec" aria-label="Start with your outcome">
        <div className="wrap">
          <div className="oc-head rise">
            <span className="label">Start with your outcome</span>
            <p className="oc-lead">What do you want AI to do first?</p>
          </div>
          <div className="oc-grid rise" style={{ animationDelay: '.05s' }}>
            {content.outcomes.map((o, i) => {
              const href = o.href || '#services'
              const inner = (
                <>
                  <span className="oc-lbl">{o.label}</span>
                  {o.sub ? <span className="oc-sub">{o.sub}</span> : null}
                  <span className="oc-go" aria-hidden="true">→</span>
                </>
              )
              return href.startsWith('#')
                ? <a key={i} href={href} className="oc-card glass" onClick={(e) => { e.preventDefault(); scrollToHash(href) }}>{inner}</a>
                : <Link key={i} to={href} className="oc-card glass">{inner}</Link>
            })}
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="mq-sec" ref={mqSecRef}>
        <div className="mq-row" ref={row1Ref}>{marqueePills}</div>
        <div className="mq-row" ref={row2Ref}>{marqueePills}</div>
      </div>

      {/* HOW IT KEEPS ANSWERS USEFUL (proof strip — headless) */}
      <section className="band" id="useful" style={{ paddingBottom: 0 }}>
        <div className="wrap">
          <span className="label rise">Grounded by design</span>
          <h2 className="h2 rise" style={{ animationDelay: '.05s', marginTop: '.5rem', maxWidth: '20ch' }}>
            How it keeps answers <span className="serif">useful &amp; safe.</span>
          </h2>
          <ol className="steps-row rise" style={{ animationDelay: '.1s' }}>
            {sections.proof.map((s, i) => (<li key={i}><b>{s.b}</b>{s.t}</li>))}
          </ol>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="manifesto" id="manifesto">
        <div className="wrap">
          <p className="reveal" ref={revealRef}><ManifestoChars text={MANIFESTO_TEXT} accent={MANIFESTO_ACCENT} /></p>
        </div>
      </section>

      {/* STICKY-STACKING DECK */}
      <section className="band" id="deck" style={{ paddingBottom: 0 }}>
        <div className="wrap deck-head">
          <span className="label rise">How it works</span>
          <h2 className="h2 rise" style={{ animationDelay: '.05s', marginTop: '.55rem', maxWidth: '20ch' }}>
            Switch one on — <span className="serif">and watch it work.</span>
          </h2>
          <p className="sub rise" style={{ animationDelay: '.1s', marginTop: '.7rem' }}>
            {content.deckLead}
          </p>
        </div>
        <div className="deck" ref={deckRef}>
          {DECK.map((c, i) => (
            <div className="slot" key={i}>
              <div className="scard glass">
                <div className="media">
                  {c.mesh ? <MeshArt chief /> : <img src={c.gif} alt={c.alt} loading="lazy" />}
                </div>
                <div className="txt">
                  <div className="num">{c.num}</div>
                  <h3>{c.pre}<span className="serif">{c.serif}</span>{c.post}</h3>
                  <p>{c.p}</p>
                  <ul>
                    {c.bullets.map((b, bi) => (<li key={bi}><CheckMark />{b}</li>))}
                  </ul>
                  <div className="tag"><b>{c.tagB}</b>{c.tag}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* EVIDENCE EXAMPLE — one source-linked Q&A that makes "Marketing Intelligence"
          tangible. ILLUSTRATIVE, not a real customer result — labeled as such. */}
      <section className="band" style={{ paddingBottom: 0 }}>
        <div className="wrap">
          <span className="label rise">{content.evidence.label}</span>
          <h2 className="h2 rise" style={{ animationDelay: '.05s', marginTop: '.5rem', maxWidth: '20ch' }}>
            {content.evidence.heading}
          </h2>
          <div className="ev-card glass rise" style={{ animationDelay: '.1s' }}>
            <span className="ev-eyebrow">{content.evidence.eyebrow || 'Example'}</span>
            <p className="ev-q"><span className="ev-qmark" aria-hidden="true">Q</span>{content.evidence.question}</p>
            <p className="ev-a"><span className="ev-amark" aria-hidden="true">A</span>{content.evidence.answer}</p>
            <p className="ev-cap">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1" /></svg>
              {content.evidence.caption}
            </p>
          </div>
        </div>
      </section>

      {/* SERVICES — grouped into the five buyer-outcome lanes (interactive commerce) */}
      <section className="band" id="services">
        <div className="wrap">
          <span className="label rise">The catalog</span>
          <h2 className="h2 rise" style={{ animationDelay: '.05s', marginTop: '.5rem', maxWidth: '22ch' }}>
            Start with the outcome. <span className="serif">Switch on the service.</span>
          </h2>
          <p className="sub rise" style={{ animationDelay: '.1s' }}>
            {services.length} services across five outcome lanes. Lead with the question you're trying to answer — then click any service to see what it does and add just what you need.
          </p>
          {/* Five outcome lanes as SIDE-BY-SIDE columns: each is a category "box"
              with a sticky header and an internally-scrollable list of services.
              The whole strip scrolls horizontally so it becomes a swipeable strip
              on tablet/mobile. Every service stays a clickable → ServiceModal card. */}
          <div className="lanes-strip rise" style={{ animationDelay: '.14s' }}>
            {groupByCluster(services).map((lane) => (
              <div className="lane-col glass" key={lane.name}>
                <div className="lane-col-head">
                  <h3 className="lane-name">{lane.name}</h3>
                  <p className="lane-q">{LANE_Q[lane.name] || lane.tagline}</p>
                  <span className="lane-count">{lane.items.length} service{lane.items.length === 1 ? '' : 's'}</span>
                </div>
                <div className="lane-col-list">
                  {lane.items.map((s) => {
                    const Icon = s.icon
                    const added = cart.some((c) => c.key === s.key)
                    return (
                      <button className="svc-row" key={s.key} onClick={() => setModalKey(s.key)}>
                        <span className="svc-ic">{Icon ? <Icon size={17} /> : <span aria-hidden="true">{(s.name || '?').charAt(0)}</span>}</span>
                        <span className="svc-row-body">
                          <span className="svc-row-top">
                            <span className="svc-nm">{s.name}</span>
                            <span className="price">{Number(s.price) === 0 ? 'Free' : `$${s.price}/mo`}</span>
                          </span>
                          <span className="svc-desc">{s.short}</span>
                          <span className={`more${added ? ' added' : ''}`}>
                            {added ? <>In your cart <Check size={13} /></> : <>See what it does <ArrowRight size={13} /></>}
                          </span>
                        </span>
                      </button>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="band" style={{ paddingTop: 0 }}>
        <div className="wrap">
          <h2 className="h2 rise" style={{ maxWidth: '16ch' }}>Grounded <span className="serif">×</span> Agentic.</h2>
          <div className="split">
            <div className="art scaleIn"><MeshArt style={{ width: 'min(80%,340px)' }} /></div>
            <div className="rise" style={{ animationDelay: '.08s' }}>
              <div className="blk">
                <div className="l">Grounded in your data</div>
                <p>Every answer traces back to something you actually published — with a groundedness score, a safe fallback for anything unsupported, and a PII-redacted audit trail. Trust isn't a claim; it's measured on every reply.</p>
              </div>
              <div className="divider" />
              <div className="blk">
                <div className="l">Powered by an agent mesh</div>
                <p>Under the surface, a Chief agent dispatches specialists — retrieval, routing, verification, escalation — each self-correcting, each on demand. A team of AI workers, not a single black box.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="band" style={{ paddingTop: 0 }}>
        <div className="wrap">
          <div className="glass rise" style={{ borderRadius: 18, padding: '26px 22px' }}>
            <div className="trust">
              {TRUST_ITEMS.map((tItem, i) => (
                <span key={i}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{tItem.path}</svg>{tItem.label}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="band" style={{ paddingTop: 0 }}>
        <div className="wrap">
          <div className="cta-wrap glass rise">
            <div className="cta-glow" />
            <h2 className="h2" style={{ maxWidth: '20ch', margin: '0 auto' }}>Turn your website into <span className="serif">AI services.</span></h2>
            <p style={{ color: 'var(--ink-2)', margin: '1rem auto 0', maxWidth: '44ch' }}>
              Bring your content, switch on what you need, pay only for what you turn on. Free to create your workspace.
            </p>
            <div className="hero-actions">
              <button className="btn btn-red btn-lg" onClick={() => scrollToHash('#services')}>
                Start free <ArrowRight size={16} />
              </button>
              <Link to="/guide" className="btn btn-ghost btn-lg glass">Read the guide</Link>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER (dark, inline — does not touch shared SiteFooter) */}
      {/* Shared site footer — same SiteFooter as every other marketing page. */}
      <SiteFooter />

      {modalSvc && (
        <ServiceModal svc={modalSvc} qty={qtyInCart(modalSvc.key)}
          onClose={() => setModalKey(null)}
          onAdd={() => addToCart(modalSvc.key, 1)}
          onSetQty={(q) => setQty(modalSvc.key, q)}
          onRemove={() => removeFromCart(modalSvc.key)}
          onViewCart={() => { setModalKey(null); setCartOpen(true) }} />
      )}

      {cartOpen && (
        <CartDrawer items={items} total={total}
          onClose={() => setCartOpen(false)}
          onSetQty={setQty}
          onRemove={removeFromCart}
          onCheckout={goCheckout} />
      )}
    </div>
  )
}

// ── Preserved interactive pieces (restyled dark): Stepper, Modal, Drawer ─────
function Stepper({ qty, onDec, onInc, small }) {
  return (
    <div className={`stepper${small ? ' small' : ''}`}>
      <button onClick={onDec} disabled={qty <= 1} aria-label="Decrease"><Minus size={14} /></button>
      <span className="n">{qty}</span>
      <button onClick={onInc} aria-label="Increase"><Plus size={14} /></button>
    </div>
  )
}

function ServiceModal({ svc, qty, onClose, onAdd, onSetQty, onRemove, onViewCart }) {
  const Icon = svc.icon
  const inCartNow = qty > 0
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])
  return (
    <div className="ov">
      <div className="ov-back" onClick={onClose} />
      <div className="modal glass">
        <button onClick={onClose} className="x" aria-label="Close"><X size={18} /></button>
        <div className="modal-top">
          <div className="modal-ic">{Icon ? <Icon size={24} /> : <span aria-hidden="true">{(svc.name || '?').charAt(0)}</span>}</div>
          <div>
            <h3>{svc.name}</h3>
            <p className="price">{Number(svc.price) === 0 ? 'Free' : `$${svc.price}/mo`}{svc.seated ? ` · per ${unitOf(svc.key)}` : ''}</p>
          </div>
        </div>
        {(svc.desc || svc.short) && <p className="desc">{svc.desc || svc.short}</p>}
        {svc.example && (
          <div className="eg">
            <p className="k">Example</p>
            <p className="v">{svc.example}</p>
          </div>
        )}
        <Link to={svc.slug ? `/products/${svc.slug}` : productHref(svc.key)} onClick={onClose} style={{ display: 'inline-flex', alignItems: 'center', gap: '.35rem', marginTop: '14px', color: 'var(--accent-ink)', fontSize: '13px', fontWeight: 600 }}>
          See the full product page <ArrowRight size={14} />
        </Link>

        {svc.seated && inCartNow && (
          <div className="seatbox">
            <div>
              <p className="st">How many {unitOf(svc.key)}s?</p>
              <p className="sd">Each runs independently for a different use case.</p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Stepper qty={qty} onDec={() => onSetQty(qty - 1)} onInc={() => onSetQty(qty + 1)} />
              <span className="amt" style={{ width: 64, textAlign: 'right' }}>${svc.price * qty}/mo</span>
            </div>
          </div>
        )}

        <div className="row-actions">
          {inCartNow ? (
            <>
              <button onClick={onRemove} className="btn btn-ghost glass"><Trash2 size={15} /> Remove</button>
              <button onClick={onViewCart} className="btn btn-red fill" style={{ justifyContent: 'center' }}><ShoppingCart size={15} /> View cart →</button>
            </>
          ) : (
            <button onClick={onAdd} className="btn btn-red btn-full"><Plus size={16} /> Add to cart · {Number(svc.price) === 0 ? 'Free' : `$${svc.price}/mo`}</button>
          )}
        </div>
        {svc.seated && !inCartNow && (
          <p className="hint">You can add more than one after it's in your cart.</p>
        )}
      </div>
    </div>
  )
}

function CartDrawer({ items, total, onClose, onSetQty, onRemove, onCheckout }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])
  return (
    <div className="ov">
      <div className="ov-back" onClick={onClose} />
      <div className="drawer">
        <div className="drawer-head">
          <h3><ShoppingCart size={18} /> Your cart</h3>
          <button onClick={onClose} className="x" aria-label="Close" style={{ position: 'static', color: 'var(--ink-3)', background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} /></button>
        </div>
        <div className="drawer-body">
          {items.length === 0 ? (
            <div className="drawer-empty">
              <ShoppingCart size={40} />
              <p style={{ fontSize: '.9rem' }}>Your cart is empty.</p>
              <button onClick={onClose} className="link-red" style={{ marginTop: 8 }}>Browse services →</button>
            </div>
          ) : items.map((s) => {
            const Icon = s.icon
            return (
              <div key={s.key} className="cart-line">
                <div className="li-ic">{Icon ? <Icon size={18} /> : <span aria-hidden="true">{(s.name || '?').charAt(0)}</span>}</div>
                <div className="li-mid">
                  <p className="li-nm">{s.name}{s.qty > 1 && <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}> × {s.qty}</span>}</p>
                  <p className="li-sub">{Number(s.price) === 0 ? 'Free' : `$${s.price}/mo`}{s.seated ? ` · per ${unitOf(s.key)}` : ''}</p>
                  {s.seated && <div style={{ marginTop: 6 }}><Stepper small qty={s.qty} onDec={() => onSetQty(s.key, s.qty - 1)} onInc={() => onSetQty(s.key, s.qty + 1)} /></div>}
                </div>
                <div>
                  <p className="li-amt">${s.price * s.qty}</p>
                  <button onClick={() => onRemove(s.key)} className="li-rm" aria-label={`Remove ${s.name}`}><Trash2 size={15} /></button>
                </div>
              </div>
            )
          })}
        </div>
        {items.length > 0 && (
          <div className="drawer-foot">
            <div className="drawer-total">
              <span className="tl">Total</span>
              <span className="tv">${total}<small>/mo</small></span>
            </div>
            <button onClick={onCheckout} className="btn btn-red btn-full">Checkout <ArrowRight size={16} /></button>
            <p className="hint" style={{ marginTop: 0 }}>You'll create your free account at checkout.</p>
          </div>
        )}
      </div>
    </div>
  )
}
